import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  EmployeeCreateInput,
  EmployeeCreateResult,
  EmployeeCurrentShift,
  EmployeeDetails,
  EmployeeLeavePolicies,
  EmployeeListFilters,
  EmployeeListItem,
  EmployeeRole,
  EmployeeSearchResult,
  EmployeeShiftAssignmentInput,
  EmployeeUpdateInput,
  EmployeeWfhEligibility,
  LeavePolicyOption,
  UpcomingBirthday,
  WfhPolicyOption,
} from '../types/employee';
import type { DashboardScope } from '../types/dashboard';
import { calendarDateFromApi, formatPermissionTime, toCalendarDateString } from '../utils/dateTime';
import {
  filterRolesForEmployeeCreate,
  filterRolesForEmployeeEdit,
} from '../utils/employeeCreateRoles';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) throw new Error(`${label} is missing`);
  return parsed;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function boolOrNull(value: unknown): boolean | null {
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return null;
}

function displayNameFrom(row: ObjectValue): string {
  const joined = [textOrNull(row.firstName), textOrNull(row.middleName), textOrNull(row.lastName)]
    .filter(Boolean)
    .join(' ')
    .trim();
  return (
    textOrNull(row.displayName) ??
    (joined || null) ??
    textOrNull(row.employeeName) ??
    textOrNull(row.fullName) ??
    textOrNull(row.name) ??
    textOrNull(row.employeeNumber) ??
    'Employee'
  );
}

function nestedObject(value: unknown): ObjectValue | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as ObjectValue)
    : null;
}

function usernameFrom(row: ObjectValue): string | null {
  const nested = nestedObject(row.user) ?? nestedObject(row.account);
  return (
    textOrNull(row.username) ??
    textOrNull(row.userName) ??
    textOrNull(row.loginUsername) ??
    textOrNull(row.loginName) ??
    textOrNull(row.userLogin) ??
    (nested
      ? textOrNull(nested.username) ??
        textOrNull(nested.userName) ??
        textOrNull(nested.loginName)
      : null)
  );
}

function personNameFrom(row: ObjectValue, employeeNumber: string): string {
  const named = displayNameFrom(row);
  if (named.trim().toLowerCase() === employeeNumber.trim().toLowerCase()) {
    return '';
  }
  return named;
}

function departmentFrom(row: ObjectValue): string | null {
  return (
    textOrNull(row.departmentName) ??
    textOrNull(row.department) ??
    textOrNull((row.department as ObjectValue | undefined)?.departmentName)
  );
}

function designationFrom(row: ObjectValue): string | null {
  return (
    textOrNull(row.designationName) ??
    textOrNull(row.designation) ??
    textOrNull((row.designation as ObjectValue | undefined)?.designationName)
  );
}

function normalizeListItem(row: ObjectValue): EmployeeListItem {
  return {
    employeeId: requiredId(row.employeeId, 'employeeId'),
    employeeNumber: textOrNull(row.employeeNumber) ?? String(row.employeeId),
    displayName: personNameFrom(
      row,
      textOrNull(row.employeeNumber) ?? String(row.employeeId),
    ),
    username: usernameFrom(row),
    department: departmentFrom(row),
    designation: designationFrom(row),
    status:
      textOrNull(row.statusName) ??
      textOrNull(row.employeeStatusName) ??
      textOrNull(row.status),
    isActive: boolOrNull(row.isActive),
    faceAttendanceEnabled: boolOrNull(row.faceAttendanceEnabled),
    // Only use list-provided registration fields. Do not fetch per row.
    faceRegistrationStatus:
      textOrNull(row.faceRegistrationStatus) ??
      textOrNull(row.registrationStatus) ??
      null,
  };
}

function clockTime(value: unknown): string | null {
  const text = textOrNull(value);
  if (!text) return null;
  const match = text.trim().match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return `${match[1].padStart(2, '0')}:${match[2]}`;
}

export function formatEmployeeShiftHours(
  start: string | null,
  end: string | null,
): string | null {
  if (start && end) {
    return `${formatPermissionTime(start)} – ${formatPermissionTime(end)}`;
  }
  if (start) return formatPermissionTime(start);
  if (end) return formatPermissionTime(end);
  return null;
}

function normalizeCurrentShift(row: ObjectValue | null): EmployeeCurrentShift | null {
  if (!row) return null;
  const shiftStartTime = clockTime(
    row.shiftStartTime ?? row.scheduledStartTime ?? row.scheduledStartAt,
  );
  const shiftEndTime = clockTime(
    row.shiftEndTime ?? row.scheduledEndTime ?? row.scheduledEndAt,
  );
  const shiftName = textOrNull(row.shiftName);
  const shiftId = numberOrNull(row.shiftId);
  if (!shiftName && !shiftStartTime && !shiftEndTime && shiftId == null) {
    return null;
  }
  return {
    shiftId,
    shiftCode: textOrNull(row.shiftCode),
    shiftName,
    shiftStartTime,
    shiftEndTime,
    shiftHours: formatEmployeeShiftHours(shiftStartTime, shiftEndTime),
  };
}

function normalizeRoles(value: unknown): EmployeeRole[] {
  if (!Array.isArray(value)) return [];
  const roles: EmployeeRole[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const row = item as ObjectValue;
    const roleId = numberOrNull(row.roleId ?? row.role_id);
    const roleName = textOrNull(row.roleName ?? row.role_name);
    if (!roleId || roleId <= 0 || !roleName) continue;
    roles.push({
      roleId,
      roleCode: textOrNull(row.roleCode ?? row.role_code) ?? '',
      roleName,
      description: textOrNull(row.description),
      isDefault: boolOrNull(row.isDefault ?? row.is_default ?? row.is_default_role) === true,
    });
  }
  return roles;
}

function normalizeDetails(row: ObjectValue): EmployeeDetails {
  return {
    employeeId: requiredId(row.employeeId, 'employeeId'),
    employeeUuid: textOrNull(row.employeeUuid),
    employeeNumber: textOrNull(row.employeeNumber) ?? String(row.employeeId),
    firstName: textOrNull(row.firstName),
    middleName: textOrNull(row.middleName),
    lastName: textOrNull(row.lastName),
    displayName: displayNameFrom(row),
    officialEmail: textOrNull(row.officialEmail),
    personalEmail: textOrNull(row.personalEmail),
    mobileNumber:
      textOrNull(row.mobileNumber) ??
      textOrNull(row.phoneNumber) ??
      textOrNull(row.mobile),
    departmentId: numberOrNull(row.departmentId),
    department: departmentFrom(row),
    designationId: numberOrNull(row.designationId),
    designation: designationFrom(row),
    primaryLocationId: numberOrNull(
      row.primaryLocationId ?? row.locationId,
    ),
    primaryLocation:
      textOrNull(row.primaryLocationName) ??
      textOrNull(row.locationName) ??
      textOrNull(row.primaryLocation),
    reportingManagerId: numberOrNull(row.reportingManagerId),
    reportingManager:
      textOrNull(row.reportingManagerName) ??
      textOrNull(row.managerName) ??
      textOrNull(row.reportingManager),
    employmentType: textOrNull(row.employmentType),
    joiningDate: calendarDateFromApi(row.joiningDate),
    dateOfBirth: calendarDateFromApi(row.dateOfBirth),
    genderCode: textOrNull(row.genderCode),
    attendanceRequired: boolOrNull(row.attendanceRequired),
    faceAttendanceEnabled: boolOrNull(row.faceAttendanceEnabled),
    wfhEligible: boolOrNull(row.wfhEligible),
    statusId: numberOrNull(row.statusId ?? row.employeeStatusId),
    status:
      textOrNull(row.statusName) ??
      textOrNull(row.employeeStatusName) ??
      textOrNull(row.status),
    isActive: boolOrNull(row.isActive),
    rowVersion: numberOrNull(row.rowVersion),
    roles: normalizeRoles(row.roles),
  };
}

export async function getAssignableRoles(
  search?: string,
  options?: { purpose?: 'create' | 'edit' },
): Promise<EmployeeRole[]> {
  const trimmed = search?.trim();
  const response = await apiClient.get<Envelope<{ items?: unknown } | unknown>>(
    API_ENDPOINTS.EMPLOYEES.ASSIGNABLE_ROLES,
    {
      params: {
        ...(trimmed ? { search: trimmed } : {}),
        ...(options?.purpose ? { purpose: options.purpose } : {}),
      },
    },
  );
  const data = response.data.data;
  const roles = Array.isArray(data)
    ? normalizeRoles(data)
    : normalizeRoles(
        data && typeof data === 'object'
          ? (data as { items?: unknown }).items
          : [],
      );
  if (options?.purpose === 'create') return filterRolesForEmployeeCreate(roles);
  if (options?.purpose === 'edit') return filterRolesForEmployeeEdit(roles);
  return roles;
}

export async function getEmployees(options: {
  page?: number;
  pageSize?: number;
  filters?: EmployeeListFilters;
} = {}): Promise<EmployeeSearchResult> {
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? 20;
  const search = options.filters?.search?.trim() || undefined;

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: unknown;
        pageSize?: unknown;
        totalRecords?: unknown;
        totalPages?: unknown;
      };
    }>
  >(API_ENDPOINTS.EMPLOYEES.LIST, {
    params: {
      page,
      pageSize,
      ...(search ? { search } : {}),
    },
  });

  const data = response.data.data ?? {};
  const pagination = data.pagination ?? {};
  const items = Array.isArray(data.items) ? data.items.map(normalizeListItem) : [];
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const resolvedPageSize = numberOrNull(pagination.pageSize) ?? pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / resolvedPageSize));

  return {
    items,
    page: numberOrNull(pagination.page) ?? page,
    pageSize: resolvedPageSize,
    totalItems,
    totalPages,
  };
}

/**
 * Loads every employee for the signed-in organization by paging the
 * employees API (max 100 per request).
 */
export async function getAllOrganizationEmployees(): Promise<EmployeeListItem[]> {
  const pageSize = 100;
  const first = await getEmployees({ page: 1, pageSize });
  const items = [...first.items];
  const totalPages = Math.max(1, first.totalPages || 1);
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await getEmployees({ page, pageSize });
    items.push(...next.items);
  }
  return items;
}

export async function getEmployeeDetails(employeeId: number): Promise<EmployeeDetails> {
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.DETAILS(employeeId),
  );
  return normalizeDetails(response.data.data ?? {});
}

async function loadShiftFromTodayAttendance(
  employeeId: number,
): Promise<EmployeeCurrentShift | null> {
  const today = toCalendarDateString(new Date());
  try {
    const response = await apiClient.get<
      Envelope<{ attendance?: ObjectValue | null; shift?: ObjectValue | null }>
    >(API_ENDPOINTS.ATTENDANCE.DETAILS(today), { params: { employeeId } });
    const data = response.data.data ?? {};
    return (
      normalizeCurrentShift(data.shift ?? null) ??
      normalizeCurrentShift(data.attendance ?? null)
    );
  } catch {
    return null;
  }
}

export async function getEmployeeCurrentShift(
  employeeId: number,
): Promise<EmployeeCurrentShift | null> {
  if (!Number.isInteger(employeeId) || employeeId <= 0) {
    throw Object.assign(new Error('employeeId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  try {
    const response = await apiClient.get<Envelope<ObjectValue | null>>(
      API_ENDPOINTS.EMPLOYEES.ASSIGN_SHIFT(employeeId),
    );
    return normalizeCurrentShift(response.data.data ?? null);
  } catch {
    return loadShiftFromTodayAttendance(employeeId);
  }
}

export async function createEmployee(
  input: EmployeeCreateInput,
): Promise<EmployeeCreateResult> {
  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.CREATE,
    input,
  );
  const data = response.data.data ?? {};
  const employeeId = numberOrNull(data.employeeId ?? data.employee_id);
  if (!employeeId) throw new Error('Employee create did not return employeeId');
  return {
    employeeId,
    employeeNumber: textOrNull(data.employeeNumber),
    displayName: textOrNull(data.displayName) ?? displayNameFrom(data),
    leavePolicies: leavePolicyList(data.leavePolicies),
    leavePolicyWarning: textOrNull(data.leavePolicyWarning),
  };
}

export async function updateEmployee(
  employeeId: number,
  input: EmployeeUpdateInput,
): Promise<EmployeeDetails> {
  if (!Number.isInteger(employeeId) || employeeId <= 0) {
    throw Object.assign(new Error('employeeId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.UPDATE(employeeId),
    input,
  );
  return normalizeDetails(response.data.data ?? { employeeId, ...input });
}

export type EmployeeDeactivateResult = {
  /** Message returned by the deactivate API. */
  message: string;
};

export async function deactivateEmployee(
  employeeId: number,
  lastWorkingDate: string,
): Promise<EmployeeDeactivateResult> {
  if (!Number.isInteger(employeeId) || employeeId <= 0) {
    throw Object.assign(new Error('employeeId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(lastWorkingDate)) {
    throw Object.assign(
      new Error('lastWorkingDate must use YYYY-MM-DD format'),
      { code: 'VALIDATION_ERROR' },
    );
  }
  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.DEACTIVATE(employeeId),
    { lastWorkingDate },
  );
  return {
    message:
      textOrNull(response.data?.message) ?? 'Employee deactivated successfully',
  };
}

export type EmployeeActivateResult = {
  /** Message returned by the activate API. */
  message: string;
};

export async function activateEmployee(
  employeeId: number,
  remarks?: string | null,
): Promise<EmployeeActivateResult> {
  if (!Number.isInteger(employeeId) || employeeId <= 0) {
    throw Object.assign(new Error('employeeId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  const trimmed = remarks?.trim() || undefined;
  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.ACTIVATE(employeeId),
    { ...(trimmed ? { remarks: trimmed } : {}) },
  );
  return {
    message:
      textOrNull(response.data?.message) ?? 'Employee activated successfully',
  };
}

export async function assignEmployeeShift(
  employeeId: number,
  input: EmployeeShiftAssignmentInput,
): Promise<void> {
  if (!Number.isInteger(employeeId) || employeeId <= 0) {
    throw Object.assign(new Error('employeeId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!Number.isInteger(input.shiftId) || input.shiftId <= 0) {
    throw Object.assign(new Error('shiftId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.effectiveFrom)) {
    throw Object.assign(
      new Error('effectiveFrom must use YYYY-MM-DD format'),
      { code: 'VALIDATION_ERROR' },
    );
  }
  await apiClient.post(API_ENDPOINTS.EMPLOYEES.ASSIGN_SHIFT(employeeId), {
    shiftId: input.shiftId,
    effectiveFrom: input.effectiveFrom,
  });
}

function normalizeWfhPolicy(value: unknown): WfhPolicyOption | null {
  const row = nestedObject(value);
  const wfhPolicyId = numberOrNull(row?.wfhPolicyId);
  if (!row || wfhPolicyId === null || wfhPolicyId <= 0) return null;
  return {
    wfhPolicyId,
    policyCode: textOrNull(row.policyCode),
    policyName: textOrNull(row.policyName) ?? textOrNull(row.policyCode) ?? `WFH policy ${wfhPolicyId}`,
    maximumDaysPerMonth: numberOrNull(row.maximumDaysPerMonth),
    maximumDaysPerYear: numberOrNull(row.maximumDaysPerYear),
    allowHalfDay: boolOrNull(row.allowHalfDay) === true,
    isDefault: boolOrNull(row.isDefault) === true,
  };
}

function normalizeWfhEligibility(
  employeeId: number,
  value: unknown,
): EmployeeWfhEligibility {
  const row = nestedObject(value) ?? {};
  const assigned = normalizeWfhPolicy(row.assignedPolicy);
  return {
    employeeId: numberOrNull(row.employeeId) ?? employeeId,
    wfhEligible: boolOrNull(row.wfhEligible) === true,
    assignedPolicy: assigned
      ? {
          ...assigned,
          assignedFrom: calendarDateFromApi(
            nestedObject(row.assignedPolicy)?.assignedFrom,
          ),
        }
      : null,
    defaultPolicy: normalizeWfhPolicy(row.defaultPolicy),
    availablePolicies: Array.isArray(row.availablePolicies)
      ? row.availablePolicies
          .map(normalizeWfhPolicy)
          .filter((item): item is WfhPolicyOption => item !== null)
      : [],
  };
}

function assertEmployeeId(employeeId: number) {
  if (!Number.isInteger(employeeId) || employeeId <= 0) {
    throw Object.assign(new Error('employeeId must be a positive integer'), {
      code: 'VALIDATION_ERROR',
    });
  }
}

/** HR admin: eligibility, assigned policy, org default and assignable policies. */
export async function getEmployeeWfhEligibility(
  employeeId: number,
): Promise<EmployeeWfhEligibility> {
  assertEmployeeId(employeeId);
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.WFH_ELIGIBILITY(employeeId),
  );
  return normalizeWfhEligibility(employeeId, response.data.data);
}

/**
 * HR admin: enable or disable WFH. Enabling without a policy lets the server
 * assign the organization's configured default WFH policy.
 */
export async function setEmployeeWfhEligibility(
  employeeId: number,
  input: { wfhEligible: boolean; wfhPolicyId?: number | null },
): Promise<EmployeeWfhEligibility> {
  assertEmployeeId(employeeId);
  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.WFH_ELIGIBILITY(employeeId),
    {
      wfhEligible: input.wfhEligible,
      ...(input.wfhEligible && input.wfhPolicyId
        ? { wfhPolicyId: input.wfhPolicyId }
        : {}),
    },
  );
  return normalizeWfhEligibility(employeeId, response.data.data);
}

/** HR admin: change the WFH policy of an eligible employee. */
export async function assignEmployeeWfhPolicy(
  employeeId: number,
  wfhPolicyId: number,
): Promise<EmployeeWfhEligibility> {
  assertEmployeeId(employeeId);
  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.WFH_POLICY(employeeId),
    { wfhPolicyId },
  );
  return normalizeWfhEligibility(employeeId, response.data.data);
}

function normalizeLeavePolicy(value: unknown): LeavePolicyOption | null {
  const row = nestedObject(value);
  const leavePolicyId = numberOrNull(row?.leavePolicyId);
  const leaveTypeId = numberOrNull(row?.leaveTypeId);
  if (!row || !leavePolicyId || leavePolicyId <= 0 || !leaveTypeId) return null;
  return {
    leavePolicyId,
    policyCode: textOrNull(row.policyCode),
    policyName:
      textOrNull(row.policyName) ??
      textOrNull(row.policyCode) ??
      `Leave policy ${leavePolicyId}`,
    leaveTypeId,
    leaveTypeName:
      textOrNull(row.leaveTypeName) ??
      textOrNull(row.leaveTypeCode) ??
      `Leave type ${leaveTypeId}`,
    annualEntitlement: numberOrNull(row.annualEntitlement),
    monthlyAccrual: numberOrNull(row.monthlyAccrual),
    carryForwardAllowed: boolOrNull(row.carryForwardAllowed) === true,
    isDefault: boolOrNull(row.isDefault) === true,
  };
}

function leavePolicyList(value: unknown): LeavePolicyOption[] {
  return Array.isArray(value)
    ? value
        .map(normalizeLeavePolicy)
        .filter((item): item is LeavePolicyOption => item !== null)
    : [];
}

function normalizeLeavePolicies(
  employeeId: number,
  value: unknown,
): EmployeeLeavePolicies {
  const row = nestedObject(value) ?? {};
  const assigned = Array.isArray(row.assignedPolicies) ? row.assignedPolicies : [];
  return {
    employeeId: numberOrNull(row.employeeId) ?? employeeId,
    assignedPolicies: assigned.flatMap(item => {
      const policy = normalizeLeavePolicy(item);
      return policy
        ? [
            {
              ...policy,
              assignedFrom: calendarDateFromApi(nestedObject(item)?.assignedFrom),
            },
          ]
        : [];
    }),
    defaultPolicies: leavePolicyList(row.defaultPolicies),
    availablePolicies: leavePolicyList(row.availablePolicies),
  };
}

/** HR admin: assigned leave policies, org defaults and assignable policies. */
export async function getEmployeeLeavePolicies(
  employeeId: number,
): Promise<EmployeeLeavePolicies> {
  assertEmployeeId(employeeId);
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.LEAVE_POLICIES(employeeId),
  );
  return normalizeLeavePolicies(employeeId, response.data.data);
}

/**
 * HR admin: set the employee's complete set of leave policies (one per leave
 * type). Leave types left out are removed from the employee.
 */
export async function setEmployeeLeavePolicies(
  employeeId: number,
  leavePolicyIds: number[],
): Promise<EmployeeLeavePolicies> {
  assertEmployeeId(employeeId);
  const response = await apiClient.put<Envelope<ObjectValue>>(
    API_ENDPOINTS.EMPLOYEES.LEAVE_POLICIES(employeeId),
    { leavePolicyIds },
  );
  return normalizeLeavePolicies(employeeId, response.data.data);
}

function normalizeUpcomingBirthday(row: ObjectValue): UpcomingBirthday | null {
  const employeeId = numberOrNull(row.employeeId);
  const employeeName = textOrNull(row.employeeName) ?? displayNameFrom(row);
  const nextBirthday = calendarDateFromApi(
    row.nextBirthday ?? row.birthdayDate ?? row.nextBirthdayDate,
  );
  const dateOfBirth = calendarDateFromApi(row.dateOfBirth ?? row.dob);
  if (
    employeeId === null ||
    employeeId <= 0 ||
    !employeeName ||
    !nextBirthday ||
    !dateOfBirth
  ) {
    return null;
  }
  return {
    employeeId,
    employeeName,
    employeeNumber: textOrNull(row.employeeNumber),
    dateOfBirth,
    nextBirthday,
  };
}

export async function getUpcomingBirthdays(options?: {
  fromDate?: string;
  limit?: number;
  scope?: DashboardScope;
}): Promise<UpcomingBirthday[]> {
  const isTeam = options?.scope === 'team';
  const params: Record<string, string | number> = {};
  if (options?.fromDate?.trim()) params.fromDate = options.fromDate.trim();
  if (
    !isTeam &&
    typeof options?.limit === 'number' &&
    Number.isInteger(options.limit) &&
    options.limit > 0
  ) {
    params.limit = options.limit;
  }
  const response = await apiClient.get<
    Envelope<{ items?: unknown } | unknown>
  >(
    isTeam
      ? API_ENDPOINTS.TEAM_DASHBOARD.BIRTHDAYS
      : API_ENDPOINTS.EMPLOYEES.UPCOMING_BIRTHDAYS,
    { params },
  );
  const data = response.data.data;
  const rows = Array.isArray(data)
    ? data
    : data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)
      ? (data as { items: unknown[] }).items
      : [];
  return rows
    .filter((row): row is ObjectValue => Boolean(row) && typeof row === 'object')
    .map(normalizeUpcomingBirthday)
    .filter((row): row is UpcomingBirthday => row !== null);
}
